import { useCallback, useEffect, useRef, useState } from 'react';
import {
  participantPostSessionApi,
  type ParticipantFeedbackPayload,
  type UpsertFeedbackDraftRequest,
} from '../../../services/api/participantPostSession';

/**
 * Debounced autosave hook for the post-session feedback form (MED-806 / AC5).
 *
 * Pattern mirrors `useReflectionAutosave` from the pre-session page:
 *   • scheduleSave merges new field values and triggers a debounced PUT.
 *   • flush forces an immediate save (used by Save Draft).
 *
 * The hook also exposes a `state` field driving the "Autosaved" / "Saving…" /
 * "Save failed" indicator inside the LeaveFeedbackCard.
 *
 * If the server returns 409 FEEDBACK_LOCKED (status was flipped to submitted
 * mid-edit, somehow), the hook surfaces `state = 'locked'` so the page can
 * switch to read-only.
 */

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error' | 'locked';

const DEBOUNCE_MS = 800;

interface UseFeedbackAutosaveOptions {
  sessionId: string | undefined;
  /** Disables autosave (e.g., after submission). */
  disabled?: boolean;
  /** Called with the latest feedback payload after every successful save. */
  onSaved?: (payload: ParticipantFeedbackPayload) => void;
}

interface UseFeedbackAutosaveReturn {
  state: AutosaveState;
  scheduleSave: (partial: UpsertFeedbackDraftRequest) => void;
  flush: () => Promise<ParticipantFeedbackPayload | null>;
}

export function useFeedbackAutosave({
  sessionId,
  disabled,
  onSaved,
}: UseFeedbackAutosaveOptions): UseFeedbackAutosaveReturn {
  const [state, setState] = useState<AutosaveState>('idle');
  const pendingRef = useRef<UpsertFeedbackDraftRequest>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const performSave = useCallback(async (): Promise<ParticipantFeedbackPayload | null> => {
    if (!sessionId) return null;
    const payload = pendingRef.current;
    if (Object.keys(payload).length === 0) return null;
    pendingRef.current = {};
    setState('saving');
    try {
      const result = await participantPostSessionApi.saveFeedbackDraft(sessionId, payload);
      setState('saved');
      onSaved?.(result);
      return result;
    } catch (err) {
      // services/api/client.ts flattens API errors to Error & { status, code };
      // the old nested e?.response?.* read was always undefined, so the locked
      // branch never fired and every 409 fell through to the generic error state.
      const apiErr = err as Error & { status?: number; code?: string };
      if (apiErr.status === 409 && apiErr.code === 'FEEDBACK_LOCKED') {
        setState('locked');
      } else {
        setState('error');
      }
      return null;
    }
  }, [sessionId, onSaved]);

  const scheduleSave = useCallback(
    (partial: UpsertFeedbackDraftRequest) => {
      if (disabled || !sessionId) return;
      pendingRef.current = { ...pendingRef.current, ...partial };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void performSave();
      }, DEBOUNCE_MS);
    },
    [disabled, sessionId, performSave],
  );

  const flush = useCallback(async (): Promise<ParticipantFeedbackPayload | null> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return performSave();
  }, [performSave]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { state, scheduleSave, flush };
}
